import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

// Remove trailing slashes from backend URL to prevent double slashes
const getBaseUrl = () => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL?.replace(/\/+$/, '');
  const apiUrl = process.env.REACT_APP_API_URL?.replace(/\/+$/, '');
  
  if (backendUrl) {
    return `${backendUrl}/api/v1`;
  }
  if (apiUrl) {
    return apiUrl;
  }
  
  return 'http://localhost:8013/api/v1';
};

const api = axios.create({
  baseURL: getBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (error?: any) => void;
}> = [];

const processQueue = (error: AxiosError | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 401 errors with token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't redirect if we're already on the login page
      if (window.location.pathname === '/login') {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers && token) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // For admin authentication, we don't have refresh tokens
        // Just redirect to login on 401, but only if not already on login page
        processQueue(null, null);
        isRefreshing = false;
        
        // Clear token and redirect to login only if not already there
        if (window.location.pathname !== '/login') {
          localStorage.removeItem('adminToken');
          // Use a small delay to prevent multiple redirects
          setTimeout(() => {
            window.location.href = '/login';
          }, 100);
        }
        return Promise.reject(new Error('Session expired. Please log in again.'));
      } catch (refreshError) {
        processQueue(refreshError as AxiosError, null);
        isRefreshing = false;
        
        // Refresh failed, logout user only if not on login page
        if (window.location.pathname !== '/login') {
          localStorage.removeItem('adminToken');
          setTimeout(() => {
            window.location.href = '/login';
          }, 100);
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default api;